import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  applyNodeChanges,
  Background,
  Controls,
  MiniMap,
  type Edge,
  type Node,
  type NodeChange,
} from 'reactflow';
import { layoutGraph } from '../server/lib/layout';
import { getContentfulField, getLocalizedValue } from '../shared/utils.js';
import { StepNode } from './StepNode';

type GraphPayload = {
  form?: unknown;
  startStep?: unknown;
  nodes?: Node[];
  edges?: Edge[];
};

function getRouteParams() {
  const pathname = window.location.pathname.replace(/\/+$/, '');
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length >= 2) {
    const slug = segments[0] || '';
    // querying by tag is case sensitive in Contentful and we currently have tags like: de-DE, en-UK, etc.
    const tag = segments[1]
      ? segments[1]
          .split('-')
          .map((part, index) =>
            index === 0 ? part.toLowerCase() : part.toUpperCase(),
          )
          .join('-')
      : '';

    const version = segments[2]?.startsWith('version=')
      ? segments[2].replace('version=', '')
      : 'unpublished';

    // Add missing version parameter to the browser URL
    if (!segments[2]) {
      const newPath = `/${slug}/${segments[1]}/version=${version}`;
      window.history.replaceState(null, '', newPath);
    }

    return { slug, tag, version };
  }

  return { slug: '', tag: '', version: '' };
}

export function App() {
  const [{ slug, tag, version }, setRoute] = useState(getRouteParams());
  const [graph, setGraph] = useState<GraphPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [startNodeId, setStartNodeId] = useState<string | null>(null);

  const nodeTypes = useMemo(() => ({ stepNode: StepNode }), []);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  const handleVersionChange = (newVersion: 'published' | 'unpublished') => {
    if (newVersion === version) return;

    const newPath = `/${slug}/${tag}/version=${newVersion}`;
    window.history.pushState(null, '', newPath);

    setRoute({
      slug,
      tag,
      version: newVersion,
    });
  };

  useEffect(() => {
    const onPopState = () => setRoute(getRouteParams());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [slug, tag, version]);

  useEffect(() => {
    if (!slug || !tag) {
      setLoading(false);
      setError(
        'Missing required parameters. Please provide both slug and market tag in the URL as follows: /slug/market-tag',
      );

      return;
    }

    const fetchGraph = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `/api/forms/${encodeURIComponent(slug)}/${encodeURIComponent(tag)}/${encodeURIComponent(version)}`,
        );
        if (!response.ok) {
          let details = '';
          const json = (await response.json()) as {
            error?: unknown;
            details?: unknown;
          };
          const err = typeof json?.error === 'string' ? json.error : '';
          const det = typeof json?.details === 'string' ? json.details : '';
          details = [err, det].filter(Boolean).join(' — ');
          const suffix = details ? `: ${details}` : '';
          throw new Error(`Unable to load form ${slug}${suffix}`);
        }
        const payload = (await response.json()) as GraphPayload;
        setGraph(payload);

        const baseNodes = payload.nodes ?? [];
        const baseEdges = payload.edges ?? [];
        const { nodes: laidOut, startId } = layoutGraph(baseNodes, baseEdges);

        setNodes(laidOut);
        setEdges(baseEdges);
        setStartNodeId(startId);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Could not load form graph';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchGraph();
  }, [slug, tag, version]);

  const title =
    (getLocalizedValue(getContentfulField(graph?.form, 'internalTitle')) as
      | string
      | undefined) ?? `Form ${slug}`;
  const startStepTitle =
    (getLocalizedValue(getContentfulField(graph?.startStep, 'internalTitle')) as
      | string
      | undefined) ?? '—';

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="mx-auto flex max-w-[90rem] flex-col gap-5">
        <header className="rounded-3xl border border-slate-200 bg-white/80 px-6 py-5 shadow-sm backdrop-blur">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-sky-600">
            Contentful Flow Visualizer
          </p>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
              <p className="mt-1 text-sm text-slate-600">
                Start step:{' '}
                <span className="font-semibold text-slate-900">
                  {startStepTitle}
                </span>
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-right">
              <div className="text-sm font-medium text-slate-900">/{slug}</div>
              <div className="mt-1 text-xs text-slate-500">
                Market: <span className="font-semibold">{tag}</span>
              </div>
            </div>
          </div>
        </header>

        {loading ? (
          <div className="rounded-3xl border border-slate-200 bg-white/80 p-8 text-center text-slate-600 shadow-sm">
            Loading graph…
          </div>
        ) : error ? (
          <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700 shadow-sm">
            {error}
          </div>
        ) : nodes.length === 0 ? (
          <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900 shadow-sm">
            No steps were returned for{' '}
            <span className="font-semibold">/{slug}</span>.
          </div>
        ) : (
          <div className="flex h-[78vh] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white/70 shadow-sm backdrop-blur">
            <div className="flex border-b border-slate-200 bg-white">
              <button
                onClick={() => handleVersionChange('unpublished')}
                className={`flex-1 border-b-2 px-6 py-4 text-sm font-medium transition ${
                  version === 'unpublished'
                    ? 'rounded-tl-3xl bg-sky-600 text-white shadow'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                Unpublished Version (Testing)
              </button>

              <button
                onClick={() => handleVersionChange('published')}
                className={`flex-1 border-b-2 px-6 py-4 text-sm font-medium transition ${
                  version === 'published'
                    ? 'rounded-tr-3xl bg-green-600 text-white shadow'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                Published Version (Live)
              </button>
            </div>

            <div className="flex-1">
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                nodeTypes={nodeTypes}
                fitView
                proOptions={{ hideAttribution: true }}
                className="h-full w-full bg-gradient-to-br from-slate-50 to-indigo-50"
                defaultEdgeOptions={{
                  type: 'smoothstep',
                  style: { strokeWidth: 2.2, stroke: '#3b82f6' },
                }}
              >
                <Background gap={18} size={1} color="#e2e8f0" />
                <MiniMap
                  pannable
                  zoomable
                  className="!rounded-xl !border !border-slate-200 !bg-white/90"
                />
                <Controls className="!rounded-xl !border !border-slate-200 !bg-white/90" />
              </ReactFlow>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
